import { computed, toValue, type Component, type MaybeRefOrGetter } from 'vue';
import { zoneSummaryFor } from '@/composables/api/useZoneSummaries';
import { modelIconForRow } from '@/composables/modelIcons';
import { readNestedId } from '@/utils/nestedRelation';

/**
 * The right mark for a zone that a record only references: the master mark
 * when other zones subscribe to it, the subscriber mark when it has a master,
 * and the plain zone mark otherwise. The rule itself is
 * {@link modelIconForRow}'s, so a zone looks the same here as it does as a
 * search result.
 *
 * A nested `{id, display_string}` reference does not carry the facts the rule
 * needs, so they come from the zone lookup, which batches every id asked for
 * in one render into one request and caches the answers. That includes
 * `is_tpg_location_zone`, which colours the zone's mark in its brand, and
 * `location_zone_status_id`, which turns it red when the zone is Pending
 * Cancelation. A
 * reference that does carry `subscribers_count` or `master_location_zone_id`
 * is used as is. Until the lookup answers, the plain zone mark shows.
 */
export function zoneIconFor(zone: unknown): Component | null {
    const reference = zone && typeof zone === 'object' ? (zone as Record<string, unknown>) : {};
    const carriesFacts = 'subscribers_count' in reference || 'master_location_zone_id' in reference;
    if (carriesFacts) {
        return modelIconForRow('LocationZone', reference);
    }
    const summary = zoneSummaryFor(readNestedId(zone));
    return modelIconForRow(
        'LocationZone',
        summary
            ? {
                  subscribers_count: summary.subscribersCount ?? 0,
                  master_location_zone_id: summary.masterLocationZoneId,
                  is_tpg_location_zone: reference.is_tpg_location_zone ?? summary.isTpg,
                  location_zone_status_id: reference.location_zone_status_id ?? summary.locationZoneStatusId,
              }
            : null,
    );
}

/** Composable form: the mark for the zone the getter returns, kept current. */
export function useZoneIcon(zone: MaybeRefOrGetter<unknown>) {
    return computed(() => zoneIconFor(toValue(zone)));
}
